/**
 * v109.9.14 — Travel · All Requests (Master Admin firehose)
 * --------------------------------------------------------
 * Master-Admin-only tab. Shows EVERY advance across every employee with:
 *   • Full context (employee + bypass + progressive status + approvers chain)
 *   • Edit any advance (bypasses the "one-approver-locks-it" rule)
 *   • Delete any advance
 *   • Search + status filter
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Pencil, Trash2, Search, ShieldCheck, ShieldPlus, Bell, RotateCcw, Check, ExternalLink } from "lucide-react";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import TravelAdvanceFormDialog from "../components/TravelAdvanceFormDialog";
import TravelSettlementFormDialog from "../components/TravelSettlementFormDialog";
import { progressiveStatus } from "./TravelAdvancesTab";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const STATUS_OPTIONS = ["all", "Submitted", "Approved", "Disbursed", "Rejected", "Settled"];

function money(v) { return `₹ ${Number(v || 0).toLocaleString("en-IN")}`; }

export default function TravelAllRequestsTab() {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editRow, setEditRow] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // v109.9.166 — MA actions on All-tab Settlements + Bookings.
  const [editStl, setEditStl] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [confirmOverride, setConfirmOverride] = useState(null);
  const [overrideRemark, setOverrideRemark] = useState("");

  const isMaster = user?.role === "master_admin";
  // v109.9.165 — Split "All" tab into 3 sub-tabs so MA sees every
  // Advance, Settlement, and Booking submitted by any employee at any
  // stage (previously the tab only listed Advances).
  const [allSub, setAllSub] = useState("advances");
  const [allSettlements, setAllSettlements] = useState([]);
  const [allBookings, setAllBookings] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const [adv, stl, bk] = await Promise.all([
        api.get("/travel-advances/all").catch(() => ({ data: [] })),
        api.get("/travel-settlements?scope=all").catch(() => ({ data: [] })),
        api.get("/travel-bookings?scope=all").catch(() => ({ data: [] })),
      ]);
      setRows(Array.isArray(adv.data) ? adv.data : []);
      setAllSettlements(Array.isArray(stl.data) ? stl.data : []);
      setAllBookings(Array.isArray(bk.data) ? bk.data : []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  useEffect(() => { if (isMaster) load(); }, [isMaster]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && (r.status || "") !== statusFilter) return false;
      if (!term) return true;
      return (
        (r.employee_name || r.created_by_name || "").toLowerCase().includes(term) ||
        (r.employee_code || "").toLowerCase().includes(term) ||
        (r.travel_purpose || "").toLowerCase().includes(term) ||
        (r.destination || "").toLowerCase().includes(term) ||
        (r.client_name || "").toLowerCase().includes(term)
      );
    });
  }, [rows, q, statusFilter]);

  const submitDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/records/accounts_advances/${confirmDelete.id}`, {
        data: { reason: deleteReason || "Master Admin deletion" },
      });
      toast.success("Travel advance deleted");
      setConfirmDelete(null); setDeleteReason("");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  /**
   * Master Admin can override the 2-tier / 4-tier approval flow entirely and
   * force the advance to `Approved` in a single click. Backend records this as
   * a `master_admin_override` slot in the approvals[] array (audit trail).
   */
  const submitOverride = async () => {
    if (!confirmOverride) return;
    try {
      await api.post(`/travel-advances/${confirmOverride.id}/step-approve`, {
        remark: overrideRemark.trim() || "Master Admin override — approval flow bypassed",
      });
      toast.success("Advance approved via Master Admin override");
      setConfirmOverride(null); setOverrideRemark("");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  if (!isMaster) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/40 p-10 text-center text-slate-500">
        <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <div className="text-[13px] font-semibold">Master Admin access required</div>
        <div className="text-[11.5px] mt-1">Only Master Admins can view every travel advance.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="travel-all-tab">
      <Tabs value={allSub} onValueChange={setAllSub}>
        <TabsList className="bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-1 gap-1">
          <TabsTrigger value="advances" data-testid="all-sub-advances">Travel Advances ({rows.length})</TabsTrigger>
          <TabsTrigger value="settlements" data-testid="all-sub-settlements">Advance Settlements ({allSettlements.length})</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="all-sub-bookings">Travel Bookings ({allBookings.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="advances" className="mt-4 space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search employee / purpose / destination / client…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8"
            data-testid="all-search"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-[11.5px] px-3 py-1.5 rounded-full border transition-all ${
                statusFilter === s
                  ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                  : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-indigo-400"
              }`}
              data-testid={`all-status-${s}`}
            >
              {s === "all" ? "All Statuses" : s}
            </button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={busy} className="ml-auto h-9 text-[11.5px]">
          {busy ? "…" : "Refresh"}
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
        {viewMode === "card" ? (
          filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">{busy ? "Loading…" : rows.length === 0 ? "No travel advances yet." : "No matches."}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3" data-testid="all-advances-cards">
              {filtered.map((r) => {
                const { label, tone } = progressiveStatus(r);
                return (
                  <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`all-card-${r.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 dark:text-white truncate">{r.employee_name || r.created_by_name}</div>
                        <div className="text-[10.5px] text-slate-500 truncate">{r.employee_code || ""}</div>
                      </div>
                      <Badge className={`${tone} text-[10px] shrink-0`}>{label}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                      <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Purpose</div><div className="text-slate-800 dark:text-slate-100 truncate">{r.travel_purpose}{r.bypass_crm ? <Badge className="ml-1.5 bg-rose-100 text-rose-700 text-[9px]">CRM Bypass</Badge> : null}</div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">From</div><div className="text-slate-700 text-[10.5px]">{(r.from_datetime || r.from_date || "").replace("T"," ").slice(0,16)}</div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">To</div><div className="text-slate-700 text-[10.5px]">{(r.to_datetime || r.to_date || "").replace("T"," ").slice(0,16)}</div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Amount</div><div className="mono font-semibold text-slate-900 dark:text-white">{money(r.advance_amount)}</div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Flow</div><div><Badge variant="outline" className="text-[10px]">{r.approval_type || "—"}</Badge></div></div>
                    </div>
                    {(r.approvals || []).length > 0 && (
                      <div className="mb-2">
                        <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Approvers</div>
                        <div className="flex flex-wrap gap-1">
                          {(r.approvals || []).map((a, i) => (
                            <Badge key={i} className={`text-[9.5px] ${a.status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : a.status === "rejected" ? "bg-rose-100 text-rose-700 border-rose-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>{a.name} · {a.status}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-100 dark:border-slate-800">
                      {r.status === "Submitted" && (
                        <Button size="sm" onClick={() => { setConfirmOverride(r); setOverrideRemark(""); }} className="h-7 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`all-override-${r.id}`}>
                          <ShieldPlus size={10} className="mr-1" /> Override
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setEditRow(r)} className="h-7 text-[10.5px]" data-testid={`all-edit-${r.id}`}><Pencil size={10} className="mr-1" /> Edit</Button>
                      <Button size="sm" variant="outline" title="Reopen for edit (resets approvals)" onClick={async () => { if(!window.confirm("Reopen this advance for edit? Approvals will be reset.")) return; try { await api.post(`/travel-advances/${r.id}/ma-reopen`); toast.success("Reopened"); load(); } catch(e){ toast.error(formatError(e.response?.data?.detail)); } }} className="h-7 text-[10.5px] text-amber-700 border-amber-200 hover:bg-amber-50" data-testid={`all-reopen-${r.id}`}><RotateCcw size={10} /></Button>
                      <Button size="sm" variant="outline" title="Nudge current approver" onClick={async () => { try { await api.post(`/travel-advances/${r.id}/nudge`); toast.success("Nudge sent"); } catch(e){ toast.error(formatError(e.response?.data?.detail)); } }} className="h-7 text-[10.5px] text-indigo-700 border-indigo-200 hover:bg-indigo-50" data-testid={`all-nudge-${r.id}`}><Bell size={10} /></Button>
                      <Button size="sm" variant="outline" onClick={() => { setConfirmDelete(r); setDeleteReason(""); }} className="h-7 text-[10.5px] text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`all-delete-${r.id}`}><Trash2 size={10} /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="all-advances-table">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10.5px] uppercase tracking-wider text-slate-600 dark:text-slate-300">
              <tr>
                <th className="p-2.5 text-left">Employee</th>
                <th className="p-2.5 text-left">Purpose</th>
                <th className="p-2.5 text-left">From → To</th>
                <th className="p-2.5 text-right">Amount</th>
                <th className="p-2.5 text-center">Flow</th>
                <th className="p-2.5 text-left">Approvers</th>
                <th className="p-2.5 text-center">Status</th>
                <th className="p-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-slate-500">
                  {busy ? "Loading…" : rows.length === 0 ? "No travel advances yet." : "No matches."}
                </td></tr>
              ) : filtered.map((r) => {
                const { label, tone } = progressiveStatus(r);
                return (
                  <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/60 dark:hover:bg-slate-800/40" data-testid={`all-row-${r.id}`}>
                    <td className="p-2.5">
                      <div className="font-semibold">{r.employee_name || r.created_by_name}</div>
                      <div className="text-[10.5px] text-slate-500">{r.employee_code || ""}</div>
                    </td>
                    <td className="p-2.5">
                      {r.travel_purpose}
                      {r.bypass_crm ? <Badge className="ml-1.5 bg-rose-100 text-rose-700 border-rose-200 text-[9.5px]">CRM Bypass</Badge> : null}
                    </td>
                    <td className="p-2.5 text-[11px]">
                      <div>{(r.from_datetime || r.from_date || "").replace("T"," ").slice(0,16)}</div>
                      <div className="text-slate-500">→ {(r.to_datetime || r.to_date || "").replace("T"," ").slice(0,16)}</div>
                    </td>
                    <td className="p-2.5 text-right font-mono font-semibold">{money(r.advance_amount)}</td>
                    <td className="p-2.5 text-center"><Badge variant="outline" className="text-[10px]">{r.approval_type || "—"}</Badge></td>
                    <td className="p-2.5">
                      <div className="flex flex-wrap gap-1">
                        {(r.approvals || []).map((a, i) => (
                          <Badge key={i}
                            className={`text-[10px] ${
                              a.status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                              a.status === "rejected" ? "bg-rose-100 text-rose-700 border-rose-200" :
                              "bg-amber-100 text-amber-800 border-amber-200"
                            }`}>
                            {a.name} · {a.status}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="p-2.5 text-center"><Badge className={`${tone} text-[10.5px]`}>{label}</Badge></td>
                    <td className="p-2.5 text-right">
                      <div className="flex justify-end gap-1">
                        {r.status === "Submitted" && (
                          <Button
                            size="sm"
                            onClick={() => { setConfirmOverride(r); setOverrideRemark(""); }}
                            className="h-7 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white"
                            title="Override 2-Tier / 4-Tier flow and approve directly"
                            data-testid={`all-override-${r.id}`}
                          >
                            <ShieldPlus size={11} className="mr-1" /> Override Approve
                          </Button>
                        )}
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setEditRow(r)}
                          className="h-7 w-7 p-0" title="Edit"
                          data-testid={`all-edit-${r.id}`}
                        >
                          <Pencil size={12} />
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => { setConfirmDelete(r); setDeleteReason(""); }}
                          className="h-7 w-7 p-0 text-rose-600 border-rose-200 hover:bg-rose-50"
                          title="Delete"
                          data-testid={`all-delete-${r.id}`}
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
        </TabsContent>

        {/* Settlements sub-tab */}
        <TabsContent value="settlements" className="mt-4">
          {allSettlements.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-[12px]">{busy ? "Loading…" : "No settlements yet."}</div>
          ) : (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 dark:bg-slate-800/60">
                  <tr className="text-left [&>th]:px-3 [&>th]:py-2 [&>th]:text-[10px] [&>th]:uppercase [&>th]:tracking-wider [&>th]:text-slate-600">
                    <th>Employee</th><th>Code</th><th>Kind</th><th>Purpose</th><th>Amount</th><th>Status</th><th>Submitted</th>
                    {isMaster && <th className="text-right">MA Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {allSettlements.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800 [&>td]:px-3 [&>td]:py-2" data-testid={`all-stl-${r.id}`}>
                      <td className="font-semibold">{r.created_by_name}</td>
                      <td className="mono text-[11px] text-slate-500">{r.settlement_code || "—"}</td>
                      <td><Badge className="text-[10px]">{r.kind || "—"}</Badge></td>
                      <td className="text-slate-700 max-w-[220px] truncate">{r.purpose_of_travel || `${r.month || ""} ${r.year || ""}`.trim()}</td>
                      <td className="mono font-semibold">{money(r.claim_amount)}</td>
                      <td><Badge className="text-[10px]">{r.status}</Badge></td>
                      <td className="text-[10.5px] text-slate-500">{(r.submitted_at || r.created_at || "").replace("T"," ").slice(0,16)}</td>
                      {isMaster && (
                        <td className="text-right">
                          <MaActionsCell
                            row={r}
                            kind="settlement"
                            onEdit={() => setEditStl(r)}
                            onReload={load}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Bookings sub-tab */}
        <TabsContent value="bookings" className="mt-4">
          {allBookings.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-[12px]">{busy ? "Loading…" : "No bookings yet."}</div>
          ) : (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 dark:bg-slate-800/60">
                  <tr className="text-left [&>th]:px-3 [&>th]:py-2 [&>th]:text-[10px] [&>th]:uppercase [&>th]:tracking-wider [&>th]:text-slate-600">
                    <th>Employee</th><th>Type</th><th>Mode</th><th>From → To</th><th>Status</th><th>Submitted</th>
                    {isMaster && <th className="text-right">MA Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {allBookings.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800 [&>td]:px-3 [&>td]:py-2" data-testid={`all-bk-${r.id}`}>
                      <td className="font-semibold">{r.created_by_name}</td>
                      <td>{r.travel_type || "—"}</td>
                      <td>{r.mode_of_travel || "—"}</td>
                      <td className="text-slate-700">{r.from_city || (r.cities?.[0]) || "—"} → {r.to_city || (r.cities?.slice(-1)?.[0]) || "—"}</td>
                      <td><Badge className="text-[10px]">{r.status}</Badge></td>
                      <td className="text-[10.5px] text-slate-500">{(r.created_at || "").replace("T"," ").slice(0,16)}</td>
                      {isMaster && (
                        <td className="text-right">
                          <MaActionsCell
                            row={r}
                            kind="booking"
                            onEdit={null}
                            onReload={load}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit dialog — Master Admin bypasses locks */}
      <TravelAdvanceFormDialog
        open={!!editRow}
        onClose={() => setEditRow(null)}
        onCreated={load}
        editRecord={editRow}
      />

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) { setConfirmDelete(null); setDeleteReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="all-delete-dialog">
          <DialogHeader>
            <DialogTitle className="heading text-rose-700">Delete Travel Advance</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-[12.5px]">
            <p>
              You&apos;re about to permanently delete{" "}
              <b>{confirmDelete?.employee_name}</b>&apos;s advance of{" "}
              <b>{money(confirmDelete?.advance_amount)}</b>.
            </p>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Reason (optional)</Label>
              <Textarea
                rows={2}
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Audit note"
                className="mt-1.5"
                data-testid="all-delete-reason"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setConfirmDelete(null); setDeleteReason(""); }}>Cancel</Button>
            <Button onClick={submitDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="all-delete-confirm">
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Master Admin override — bypass 2-tier / 4-tier flow */}
      <Dialog open={!!confirmOverride} onOpenChange={(v) => { if (!v) { setConfirmOverride(null); setOverrideRemark(""); } }}>
        <DialogContent className="max-w-md" data-testid="all-override-dialog">
          <DialogHeader>
            <DialogTitle className="heading text-emerald-700 flex items-center gap-2">
              <ShieldPlus size={16} /> Override 2-Tier / 4-Tier &amp; Approve
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-[12.5px]">
            <p>
              You are about to <b>bypass the entire approval flow</b> and mark{" "}
              <b>{confirmOverride?.employee_name}</b>&apos;s advance of{" "}
              <b>{money(confirmOverride?.advance_amount)}</b> as{" "}
              <b>Approved</b>. This action is audit-logged.
            </p>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Override remark (optional)</Label>
              <Textarea
                rows={2}
                value={overrideRemark}
                onChange={(e) => setOverrideRemark(e.target.value)}
                placeholder="Reason for bypass — e.g. urgent field visit / customer escalation"
                className="mt-1.5"
                data-testid="all-override-remark"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setConfirmOverride(null); setOverrideRemark(""); }}>Cancel</Button>
            <Button onClick={submitOverride} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="all-override-confirm">
              Confirm Override &amp; Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v109.9.166 — Settlement edit dialog (MA override) */}
      {editStl && (
        <TravelSettlementFormDialog
          open
          onClose={() => setEditStl(null)}
          onSaved={() => { setEditStl(null); load(); }}
          editRecord={editStl}
        />
      )}
    </div>
  );
}

// v109.9.166 — MA-only action group shown at the end of each Settlement /
// Booking row in the Travel All tab. Buttons: Edit · Approve · Reopen · Nudge · Delete.
function MaActionsCell({ row, kind, onEdit, onReload }) {
  const base = kind === "settlement" ? "/travel-settlements" : "/travel-bookings";
  const call = async (path, verb = "post", confirmMsg = null, ok = "Done") => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      const fn = verb === "delete" ? api.delete : api.post;
      await fn(`${base}/${row.id}${path}`);
      toast.success(ok);
      onReload && onReload();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const canApprove = row.status && !["Approved", "Ledgered", "Reimbursed", "Rejected", "Settled"].includes(row.status);
  const canReopen = row.status && row.status !== "Draft";
  return (
    <div className="inline-flex items-center gap-1 justify-end" data-testid={`ma-actions-${kind}-${row.id}`}>
      {onEdit && (
        <button type="button" onClick={onEdit} className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700" title="Edit (MA override — audit fields locked)" data-testid={`ma-edit-${row.id}`}>
          <Pencil size={11} />
        </button>
      )}
      {kind === "booking" && (
        <a href={`/m/travel_booking?open=${row.id}`} className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700" title="Open in Travel Booking module" data-testid={`ma-open-${row.id}`}>
          <ExternalLink size={11} />
        </a>
      )}
      {canApprove && (
        <button type="button" onClick={() => call("/ma-approve", "post", `Override-approve this ${kind}? All pending approvals will be marked Approved.`, "Approved")} className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-emerald-200 hover:bg-emerald-50 text-emerald-700" title="Approve on behalf" data-testid={`ma-approve-${row.id}`}>
          <Check size={11} />
        </button>
      )}
      {canReopen && (
        <button type="button" onClick={() => call("/ma-reopen", "post", `Reopen this ${kind} for edit? Approvals will be reset and the owner will need to resubmit.`, "Reopened")} className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-amber-200 hover:bg-amber-50 text-amber-700" title="Reopen for edit" data-testid={`ma-reopen-${row.id}`}>
          <RotateCcw size={11} />
        </button>
      )}
      <button type="button" onClick={() => call("/nudge", "post", null, "Nudge sent")} className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-indigo-200 hover:bg-indigo-50 text-indigo-700" title="Nudge current approver" data-testid={`ma-nudge-${row.id}`}>
        <Bell size={11} />
      </button>
      <button type="button" onClick={() => call("", "delete", `Delete this ${kind} permanently? This cannot be undone.`, "Deleted")} className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-rose-200 hover:bg-rose-50 text-rose-700" title="Delete (hard)" data-testid={`ma-delete-${row.id}`}>
        <Trash2 size={11} />
      </button>
    </div>
  );
}
